import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CalendarCheck,
  Plus,
  Bell,
  ArrowRight,
  Globe,
  EyeOff,
} from "lucide-react";
import {
  getEventListByOrganizer,
  getAllEventsByOrganizerId,
  updateEvent,
  updateEventBySlug,
  activateEventBySlug,
  getAllBookingByOrganizer,
} from "../../api";
import Background from "../Login/Background";
import PulseLoader from "react-spinners/PulseLoader";
import EventCardV3 from "../../components/common/EventCard/EventCardV3";
import { useNightMode } from "../../contexts/NightModeContext";
import { getOrganizerCurrency } from "../../utils/currency";
import OrganizerSearchBar from "../../components/organizer/OrganizerSearchBar";

interface ActivityItem {
  id: string;
  type: "sale" | "creation" | "checkin" | "status_change";
  message: string;
  time: string;
}

const isBeforeToday = (dateStr: string) => {
  if (!dateStr) return false;
  const parts = dateStr.split("-");
  if (parts.length === 3) {
    const eventDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate < today;
  }
  const eventDate = new Date(dateStr);
  if (isNaN(eventDate.getTime())) return false;
  eventDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventDate < today;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { isNightMode } = useNightMode();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<any[]>([]);
  const [summaryStats, setSummaryStats] = useState<any>(null);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    eventId: number;
    slugId?: string;
    eventName: string;
    action: "publish" | "unpublish";
  } | null>(null);

  const authUser = localStorage.getItem("authUser");
  let organizerId: number | null = null;
  let isEventSecondAdmin = false;
  let userEventIds: any[] = [];
  let isRole2 = false;
  let userRole = "";

  try {
    if (authUser) {
      const parsedData = JSON.parse(authUser);
      userRole = String(parsedData.user?.userRole ?? "");
      isEventSecondAdmin = userRole === "8" || userRole === "10";
      isRole2 = userRole === "2";
      organizerId = parsedData.user?.organizerId ? Number(parsedData.user.organizerId) : null;
      userEventIds = parsedData.user?.eventId || [];
    }
  } catch (err) {
    console.error("Error parsing authUser", err);
  }

  const fetchData = async () => {
    try {
      setLoading(true);

      if (isEventSecondAdmin && organizerId) {
        const res = await getAllEventsByOrganizerId(organizerId);
        let allEvents = res?.events || [];
        if (userRole === "8") {
          allEvents = allEvents.filter((e: any) =>
            userEventIds.map(String).includes(String(e.eventId))
          );
        }
        setEvents(allEvents);
      } else {
        const res = await getEventListByOrganizer();
        const allEvents = res?.data?.events || res?.events || [];
        setEvents(allEvents);
        setSummaryStats(res?.data?.summary || res?.summary || null);
      }

      try {
        const bookingsRes = await getAllBookingByOrganizer();
        if (Array.isArray(bookingsRes)) {
          const mapped: ActivityItem[] = bookingsRes.slice(0, 6).map((b: any, idx: number) => ({
            id: b.bookingId || `act-${idx}`,
            type: "sale",
            message: `Ticket purchased by ${b.firstName || "Customer"} for "${b.event?.eventName || "Event"}"`,
            time: b.createdAt || new Date().toISOString(),
          }));
          setActivities(mapped);
        }
      } catch {
        setActivities([
          { id: "act-1", type: "creation", message: "Dashboard initialized successfully.", time: new Date().toISOString() },
        ]);
      }
    } catch (err) {
      console.error("Failed to load dashboard data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [organizerId]);

  const activeEvents = events.filter(e => (e.eventStatus === "active") && !isBeforeToday(e.eventDate));
  const draftEvents = events.filter(e => e.eventStatus === "draft");
  const completedEvents = events.filter(e => e.eventStatus === "closed" || (e.eventStatus !== "draft" && isBeforeToday(e.eventDate)));
  const inactiveEvents = events.filter(e => e.eventStatus === "inactive" && !isBeforeToday(e.eventDate));

  const activeEventsCount = activeEvents.length;
  const draftEventsCount = draftEvents.length;
  const upcomingEventsCount = activeEvents.length;
  const totalRevenue = summaryStats?.totalOrganizerRevenue ?? events.reduce((sum: number, e: any) => sum + (e?.revenue?.totalOrganizerRevenue || 0), 0);
  const totalTicketsSold = summaryStats?.totalBookings ?? events.reduce((sum: number, e: any) => sum + (e?.revenue?.totalBookings || 0), 0);

  const currencyInfo = getOrganizerCurrency();
  const formatCurrency = (val: number) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: currencyInfo.code, maximumFractionDigits: 0 }).format(val);
    } catch {
      return `${currencyInfo.symbol}${val.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    }
  };

  const actionableNotifications = [
    { id: "notif-1", type: "warning", text: "Draft event requires review before launching checkout.", action: "Review Drafts" },
    { id: "notif-2", type: "info", text: "MFA Secure session connected.", action: "View Status" },
    { id: "notif-3", type: "success", text: "Stripe payout system online and ready.", action: "Check Payouts" },
  ];

  const metrics = [
    { label: "Active Events", value: activeEventsCount, hint: "Live published campaigns", hintClass: "text-orange" },
    { label: "Draft Workspaces", value: draftEventsCount, hint: "Ready-to-launch setups", hintClass: "text-amber" },
    { label: "Tickets Distributed", value: totalTicketsSold.toLocaleString(), hint: "Total attendance registrations", hintClass: "text-green" },
    { label: "Upcoming Events", value: upcomingEventsCount, hint: "Active future campaigns scheduled", hintClass: "text-blue" },
  ];

  const handleUpdateStatus = async () => {
    if (!confirmDialog) return;
    const { eventId, slugId, action } = confirmDialog;
    try {
      if (action === "publish") {
        slugId ? await activateEventBySlug(slugId) : await updateEvent(eventId, { eventStatus: "active" });
      } else {
        slugId ? await updateEventBySlug(slugId, { eventStatus: "draft" }) : await updateEvent(eventId, { eventStatus: "draft" });
      }
      await fetchData();
    } catch (err) {
      console.error("Failed to update event status", err);
    } finally {
      setConfirmDialog(null);
    }
  };



  // Event list sorting; search/filter lives in Spotlight (Events group)
  const allSorted = [...events].sort((a, b) => {
    if (a.eventStatus === "draft" && b.eventStatus !== "draft") return -1;
    if (a.eventStatus !== "draft" && b.eventStatus === "draft") return 1;
    return 0;
  });

  const getBadgeClass = (type: string): string => {
    if (type === "sale") return "badge-sale";
    if (type === "creation") return "badge-creation";
    if (type === "checkin") return "badge-checkin";
    if (type === "status_change") return "badge-status";
    return "badge-default";
  };

  return (
    <div
      className={`eventox-dashboard position-relative ${isNightMode ? 'text-white' : 'text-dark'} dashboard-mobile-offset`}
      style={{ zIndex: 5, overflowX: "hidden", width: "100%" }}
    >
      <Background />

      <div className="position-relative py-3 eventox-container d-flex flex-column gap-4" style={{ zIndex: 10, maxWidth: 1440, margin: "0 auto", width: "100%" }}>

        {/* Top Utility Bar */}
        <div className="eventox-topbar">
          <div>
            <span className="font-mono fs-10px text-orange fw-semibold text-uppercase ls-2 d-inline-block">
              Organizer Workspace
            </span>
            <p className="font-mono fs-12px text-white-40 mb-0 mt-1">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </p>
          </div>

          <div className="eventox-actions">
            {/* Opens Spotlight: pages + your events + quick actions */}
            <OrganizerSearchBar />

            <div className="position-relative">
              {/* <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="icon-btn d-flex align-items-center justify-content-center position-relative border-0"
                title="Notifications"
              >
                <Bell style={{ width: 18, height: 18 }} className="text-white-70" />
                <span className="notif-dot" />
              </button> */}

              {showNotifications && (
                <div className="notif-panel">
                  <div className="d-flex justify-content-between align-items-center pb-2 mb-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <span className="fs-12px fw-bold text-white text-uppercase ls-1">Actionable Inbox</span>
                    <span className="fs-10px font-mono text-white-40">{actionableNotifications.length} items</span>
                  </div>
                  <div className="d-flex flex-column gap-2" style={{ maxHeight: 300, overflowY: "auto" }}>
                    {actionableNotifications.map((notif) => (
                      <div key={notif.id} className="notif-item">
                        <div className="d-flex gap-2 align-items-start">
                          <span className="notif-item-dot" />
                          <p className="mb-0 fs-12px text-white-70" style={{ lineHeight: 1.5 }}>{notif.text}</p>
                        </div>
                        <button
                          onClick={() => {
                            setShowNotifications(false);
                            if (notif.id === "notif-1") navigate("/events");
                            else if (notif.id === "notif-3") navigate("/payouts");
                          }}
                          className="btn btn-link p-0 fs-11px fw-semibold text-orange text-decoration-none d-flex align-items-center gap-1 mt-2 ms-auto"
                        >
                          {notif.action} <ArrowRight style={{ width: 12, height: 12 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {isRole2 && (
              <Link
                to="/create-quick-event-v2"
                className="btn btn-brand-gradient d-flex align-items-center gap-2 text-white eventox-create-btn"
              >
                <Plus style={{ width: 16, height: 16 }} /> Create Event
              </Link>
            )}
          </div>
        </div>

        {/* Spotlight Revenue Card */}
        {isRole2 && (
          <div className="eventox-spotlight position-relative">
            <div className="eventox-spotlight-dots position-absolute top-0 start-0 w-100 h-100 pe-none" />
            <div className="eventox-spotlight-glow position-absolute pe-none" />

            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-4 position-relative" style={{ zIndex: 10 }}>
              {loading ? (
                <>
                  <div style={{ width: "100%", maxWidth: 420 }}>
                    <div className="skeleton-box mb-2" style={{ height: 14, width: 140 }} />
                    <div className="skeleton-box" style={{ height: 20, width: "85%" }} />
                  </div>
                  <div className="text-start text-md-end w-100">
                    <div className="skeleton-box mb-2 ms-md-auto" style={{ height: 14, width: 160 }} />
                    <div className="skeleton-box ms-md-auto" style={{ height: 48, width: 220 }} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ maxWidth: 420 }}>
                    <span className="font-mono fs-10px text-orange fw-semibold text-uppercase ls-2 d-block mb-2">
                      Collection Activity
                    </span>
                    <p className="mb-0 text-white-70 fs-14px" style={{ lineHeight: 1.6 }}>
                      You’ve collected this amount from{" "}
                      <span className="text-white fw-bold font-mono">{events.length}</span>{" "}
                      {events.length === 1 ? "event" : "events"} through our platform.
                    </p>
                  </div>
                  <div className="text-start text-md-end w-100">
                    <span className="fs-10px font-mono text-white-40 text-uppercase ls-2 d-block mb-2">
                      Total Funds Generated
                    </span>
                    <h1 className="eventox-spotlight-amount">{formatCurrency(totalRevenue)}</h1>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Performance Stats Grid */}
        <div>
          <h2 className="fs-11px font-mono text-uppercase ls-2 text-white-40 mb-3">
            Business Performance Stats
          </h2>
          <div className="eventox-stats-grid">
            {loading ? (
              [1, 2, 3, 4].map((n) => (
                <div key={n} className="glass-panel eventox-metric-card d-flex flex-column justify-content-between">
                  <div>
                    <div className="skeleton-box mb-2" style={{ height: 12, width: "60%" }} />
                    <div className="skeleton-box mb-1" style={{ height: 28, width: "40%" }} />
                  </div>
                  <div className="skeleton-box" style={{ height: 10, width: "75%" }} />
                </div>
              ))
            ) : (
              metrics.map((m, i) => (
                <div key={i} className="glass-panel eventox-metric-card d-flex flex-column justify-content-between">
                  <div>
                    <span className="fs-11px text-white-50 text-uppercase ls-1 fw-semibold">{m.label}</span>
                    <h3 className="eventox-metric-value fw-bold text-white mb-0 mt-1 lh-1 font-mono">{m.value}</h3>
                  </div>
                  <span className={`fs-10px font-mono fw-medium ${m.hintClass}`}>{m.hint}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Organizer Event Board */}
        <div>
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center pb-2 mb-3 gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <h3 className="d-flex align-items-center gap-2 mb-0 fs-16px fw-bold text-white">
              <CalendarCheck style={{ width: 18, height: 18 }} className="text-pink" /> Organizer Event Board
            </h3>
            <span className="fs-12px font-mono text-white-40">
              {loading ? "..." : `${events.length} ${events.length === 1 ? "event" : "events"} total (including drafts)`}
            </span>
          </div>

          {loading ? (
            <div className="eventox-events-grid">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="glass-panel p-3 d-flex flex-column gap-3 rounded-20" style={{ minHeight: 280 }}>
                  <div className="skeleton-box w-100" style={{ height: 140, borderRadius: 14 }} />
                  <div className="skeleton-box" style={{ height: 18, width: "75%" }} />
                  <div className="skeleton-box" style={{ height: 12, width: "50%" }} />
                  <div className="mt-auto d-flex justify-content-between align-items-center pt-2">
                    <div className="skeleton-box" style={{ height: 14, width: "35%" }} />
                    <div className="skeleton-box" style={{ height: 28, width: "30%", borderRadius: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : allSorted.length === 0 ? (
            <div className="glass-panel empty-state">
              No events configured yet. Click &apos;Create Event&apos; above to get started!
            </div>
          ) : (
            <div className="eventox-events-grid">
              {allSorted.map((evt: any) => {
                const draft = evt.eventStatus === "draft";
                const rev = evt?.revenue?.totalOrganizerRevenue ?? 0;
                const sold = evt?.revenue?.totalBookings ?? 0;
                const cap = evt?.capacity ?? 0;
                return (
                  <EventCardV3
                    key={evt.eventId}
                    event={evt}
                    isDraft={draft}
                    revenue={rev}
                    ticketsSold={sold}
                    capacity={cap}
                    onPress={() => {
                      localStorage.setItem("eventData", JSON.stringify(evt));
                      sessionStorage.setItem("eventNavSource", "/home");
                      if (draft) {
                        navigate(`/events/${evt.eventId}/preview`, { state: { from: "/home" } });
                      } else {
                        navigate(`/event/${evt.eventId}`, { state: { from: "/home" } });
                      }
                    }}
                    onEdit={(e: any) => {
                      e.stopPropagation();
                      localStorage.setItem("eventData", JSON.stringify(evt));
                      sessionStorage.setItem("eventNavSource", "/home");
                      navigate(`/event/${evt.eventId}/edit-event`, { state: { from: "/home" } });
                    }}
                    onPublish={(e: any) => {
                      e.stopPropagation();
                      setConfirmDialog({ eventId: Number(evt.eventId), slugId: evt.slugId, eventName: evt.eventName || "Untitled", action: "publish" });
                    }}
                    onUnpublish={(e: any) => {
                      e.stopPropagation();
                      setConfirmDialog({ eventId: Number(evt.eventId), slugId: evt.slugId, eventName: evt.eventName || "Untitled", action: "unpublish" });
                    }}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activity Log */}
        {/* <div className="glass-panel p-3 p-md-4">
          <div className="d-flex justify-content-between align-items-center pb-3 mb-3 flex-wrap gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <h3 className="fs-11px font-mono text-uppercase ls-2 text-white-40 mb-0">
              Recent System Activity Log
            </h3>
            <span className="fs-10px font-mono text-white-40">Real-Time Sync Feed</span>
          </div>

          {activities.length === 0 ? (
            <p className="text-center py-3 mb-0 fs-12px text-white-40">No recent activities logged.</p>
          ) : (
            <div className="eventox-activity-grid">
              {activities.slice(0, 6).map((act) => (
                <div key={act.id} className="activity-card d-flex flex-column justify-content-between gap-3 text-start">
                  <div>
                    <span className={`activity-badge text-uppercase ${getBadgeClass(act.type)}`}>
                      {act.type}
                    </span>
                    <p className="mb-0 fs-12px text-white-70 mt-1" style={{ lineHeight: 1.5 }}>{act.message}</p>
                  </div>
                  <span className="fs-9px font-mono text-white-40">
                    {new Date(act.time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })} &middot; {new Date(act.time).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div> */}

        {/* Confirm Dialog */}
        {confirmDialog && (
          <div className="confirm-overlay d-flex align-items-center justify-content-center p-3">
            <div className="confirm-dialog">
              <div className="confirm-dialog-top-bar" />

              <div className="d-flex align-items-start gap-3">
                <div
                  className={`confirm-icon-wrap d-flex align-items-center justify-content-center ${
                    confirmDialog.action === "publish" ? "confirm-icon-publish" : "confirm-icon-unpublish"
                  }`}
                >
                  {confirmDialog.action === "publish"
                    ? <Globe style={{ width: 20, height: 20 }} className="text-green" />
                    : <EyeOff style={{ width: 20, height: 20 }} className="text-pink" />
                  }
                </div>
                <div>
                  <h3 className="fs-14px fw-bold text-white text-uppercase font-mono mb-1">
                    {confirmDialog.action === "publish" ? "Publish Event?" : "Unpublish Event?"}
                  </h3>
                  <p className="fs-12px text-white-70 mb-0" style={{ lineHeight: 1.5 }}>
                    {confirmDialog.action === "publish"
                      ? `Are you sure you want to publish "${confirmDialog.eventName}"? This will activate the event page and start accepting customer ticket checkouts.`
                      : `Are you sure you want to unpublish "${confirmDialog.eventName}"? This will revert the event to Draft status and stop accepting new checkouts.`
                    }
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
                <button
                  type="button"
                  className="confirm-btn-cancel"
                  onClick={() => setConfirmDialog(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`confirm-btn-confirm ${confirmDialog.action === "publish" ? "confirm-btn-publish" : "confirm-btn-unpublish"}`}
                  onClick={handleUpdateStatus}
                >
                  {confirmDialog.action === "publish" ? "Confirm & Publish" : "Confirm & Unpublish"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Dashboard;